import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SEO } from '../components/SEO';
import { Logo } from '../components/Logo';
import { LangSwitcher } from '../components/LangSwitcher';
import { AdSlot } from '../components/AdSlot';
import { PublicFooter } from '../components/PublicFooter';
import { Trophy, Users, BarChart2, Award, CheckCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { useState, useEffect } from 'react';
import { usePublicFreeLimits } from '../hooks/usePublicFreeLimits';
import { localizedPath } from '../i18n/slugMap';
import type { AppLocale } from '../i18n/locales';
import { useLocalizedSeo } from '../hooks/useLocalizedSeo';
import { ogImageUrl } from '../config/og-cards';
import type { FreeLimits } from '../types';

export interface CompetitionLandingConfig {
  /** Slug usado na URL (ex: "brasileirao") */
  slug: string;
  /** Nome da competição para SEO */
  seoTitle: string;
  /** Descrição para SEO */
  seoDescription: string;
  /** Keywords SEO */
  seoKeywords: string;
  /** URL canônica (fallback estático PT-BR) */
  canonical: string;
  /** Release C — id da rota em ROUTE_SLUGS: deriva canonical/hreflang por idioma */
  routeId: string;
  /** Título do hero */
  heroTitle: string;
  /** Subtítulo do hero */
  heroSubtitle: string;
  /** Cor de destaque (classe TailwindCSS) */
  accentColor: string;
  /** Fatos/destaques da competição — exibidos como lista */
  facts?: string[];
  /** FAQs específicas da competição — answer pode ser string estática ou função que recebe os limites do plano free */
  faqs: { question: string; answer: string | ((limits: FreeLimits) => string) }[];
  /** Schema.org JSON-LD adicional */
  jsonLd?: object;
}

// `routeId` referencia ROUTE_SLUGS; href é resolvido em runtime via
// localizedPath(routeId, locale) pra abrir a landing no idioma corrente.
const OTHER_COMPETITIONS = [
  { labelKey: 'interlink.comp.copa-do-mundo', label: 'Copa do Mundo 2026', routeId: 'pool-copa-do-mundo', slug: 'copa-do-mundo' },
  { labelKey: 'interlink.comp.brasileirao', label: 'Brasileirão 2026', routeId: 'pool-brasileirao', slug: 'brasileirao' },
  { labelKey: 'interlink.comp.copa-do-brasil', label: 'Copa do Brasil 2026', routeId: 'pool-copa-do-brasil', slug: 'copa-do-brasil' },
  { labelKey: 'interlink.comp.libertadores', label: 'Copa Libertadores 2026', routeId: 'pool-libertadores', slug: 'libertadores' },
  { labelKey: 'interlink.comp.champions-league', label: 'Champions League 2026', routeId: 'pool-champions-league', slug: 'champions-league' },
  { labelKey: 'interlink.comp.carioca', label: 'Campeonato Carioca 2026', routeId: 'pool-carioca', slug: 'carioca' },
  { labelKey: 'interlink.comp.paulista', label: 'Campeonato Paulista 2026', routeId: 'pool-paulista', slug: 'paulista' },
  { labelKey: 'interlink.comp.estaduais', label: 'Campeonatos Estaduais 2026', routeId: 'pool-estaduais', slug: 'estaduais' },
];

/** Resposta padrão sobre o plano free — usada nas FAQs "É gratuito?" das landing pages */
export const freePlanFaqAnswer = (limits: FreeLimits): string =>
  `O Bolão Elite tem plano gratuito e plano Premium. No gratuito você cria ${limits.maxPrivatePoolsCreated} bolão privado e participa de até ${limits.maxPrivatePoolsJoined} como convidado, com até ${limits.maxPrivatePoolMembers} participantes cada. O Premium remove todas as restrições: bolões ilimitados, participantes ilimitados e muito mais.`;

/** Resposta padrão sobre criar bolão privado — usada nas FAQs das landing pages */
export const freePlanCreateFaqAnswer = (limits: FreeLimits): string =>
  `Sim! Ao criar um bolão privado, você recebe um link de convite exclusivo. No plano gratuito você cria ${limits.maxPrivatePoolsCreated} bolão privado com até ${limits.maxPrivatePoolMembers} participantes. O plano Premium permite bolões e participantes ilimitados.`;

const FEATURES = [
  { icon: Trophy, titleKey: 'chrome.feature_1_title', titleDefault: 'Palpites Fáceis', descKey: 'chrome.feature_1_desc', descDefault: 'Faça seus palpites em segundos antes de cada rodada' },
  { icon: BarChart2, titleKey: 'chrome.feature_2_title', titleDefault: 'Rankings em Tempo Real', descKey: 'chrome.feature_2_desc', descDefault: 'Acompanhe sua posição no bolão após cada jogo' },
  { icon: Users, titleKey: 'chrome.feature_3_title', titleDefault: 'Bolões Privados ou Públicos', descKey: 'chrome.feature_3_desc', descDefault: 'Crie seu grupo de amigos ou entre em bolões abertos' },
  { icon: Award, titleKey: 'chrome.feature_4_title', titleDefault: 'Conquistas e Gamificação', descKey: 'chrome.feature_4_desc', descDefault: 'Ganhe medalhas por acertar placares exatos e mais' },
];

const HOW_STEPS = [
  { step: '1', titleKey: 'chrome.how_1_title', titleDefault: 'Crie sua conta grátis', descKey: 'chrome.how_1_desc', descDefault: 'Cadastre-se em menos de 1 minuto, sem dados de cartão.' },
  { step: '2', titleKey: 'chrome.how_2_title', titleDefault: 'Crie ou entre em um bolão', descKey: 'chrome.how_2_desc', descDefault: 'Monte seu grupo ou participe de bolões públicos abertos a todos.' },
  { step: '3', titleKey: 'chrome.how_3_title', titleDefault: 'Faça seus palpites', descKey: 'chrome.how_3_desc', descDefault: 'Palpite no placar de cada jogo antes do apito inicial.' },
  { step: '4', titleKey: 'chrome.how_4_title', titleDefault: 'Acompanhe o ranking', descKey: 'chrome.how_4_desc', descDefault: 'Pontos são calculados automaticamente após cada partida.' },
];

const STATS = [
  { valueKey: 'chrome.stat_1_value', valueDefault: '100%', labelKey: 'chrome.stat_1_label', labelDefault: 'Gratuito' },
  { valueKey: 'chrome.stat_2_value', valueDefault: 'Tempo real', labelKey: 'chrome.stat_2_label', labelDefault: 'Atualização de pontos' },
  { valueKey: 'chrome.stat_3_value', valueDefault: 'Mobile', labelKey: 'chrome.stat_3_label', labelDefault: 'Otimizado para celular' },
];

function FaqItem({ question, answer }: { question: string; answer: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
      >
        <span className="font-medium text-gray-900">{question}</span>
        {open ? <ChevronUp className="w-5 h-5 text-gray-400 flex-shrink-0" /> : <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />}
      </button>
      {open && (
        <div className="px-4 pb-4">
          <p className="text-sm text-gray-600 leading-relaxed">{answer}</p>
        </div>
      )}
    </div>
  );
}

export function CompetitionLanding({ config }: { config: CompetitionLandingConfig }) {
  const { t, i18n } = useTranslation('landing');
  // Resolve hrefs dos cross-links no idioma corrente (cards "Explore other
  // available pools" abrem /en/<slug-en> em vez de cair sempre em /<slug-pt>).
  const locale: AppLocale =
    i18n.language === 'en' ? 'en'
      : i18n.language === 'es' ? 'es'
      : 'pt-BR';
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { limits } = usePublicFreeLimits();
  const seo = useLocalizedSeo(config.routeId);

  // Prefixo i18n por landing — namespace 'landing', chaves comp.<slug>.*
  const ck = (suffix: string, def: string) => t(`comp.${config.slug}.${suffix}`, def);

  // FAQs: pergunta sempre via t(); resposta resolve a função p/ obter o
  // default PT-BR e passa os limites como params de interpolação — a
  // tradução EN/ES usa placeholders {{maxPrivatePools*}}.
  const resolvedFaqs = config.faqs.map((faq, i) => {
    const rawAnswer =
      typeof faq.answer === 'function' ? faq.answer(limits) : faq.answer;
    return {
      question: ck(`faq_${i}_q`, faq.question),
      answer: t(`comp.${config.slug}.faq_${i}_a`, {
        defaultValue: rawAnswer,
        ...limits,
      }),
    };
  });

  // Scroll to top sempre que a página for montada (incluindo navegação entre landing pages)
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
  }, []);

  const handleAnchorClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    setMobileMenuOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const navLinks = [
    { label: t('chrome.nav_features', 'Funcionalidades'), href: '#funcionalidades' },
    { label: t('chrome.nav_how', 'Como Funciona'), href: '#como-funciona' },
    { label: t('chrome.nav_faq', 'FAQ'), href: '#faq' },
  ];

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: config.seoTitle,
      description: config.seoDescription,
      url: seo.canonical,
      datePublished: '2026-03-16',
      dateModified: '2026-04-27',
      inLanguage: seo.locale,
      author: { '@id': 'https://bolaoelite.com.br/#organization' },
      publisher: { '@id': 'https://bolaoelite.com.br/#organization' },
      isPartOf: { '@id': 'https://bolaoelite.com.br/#website' },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      inLanguage: seo.locale,
      datePublished: '2026-03-16',
      dateModified: '2026-04-27',
      mainEntity: resolvedFaqs.map(faq => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: { '@type': 'Answer', text: faq.answer },
      })),
    },
    ...(config.jsonLd ? [config.jsonLd] : []),
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={ck('seo_title', config.seoTitle)}
        description={ck('seo_desc', config.seoDescription)}
        keywords={ck('seo_keywords', config.seoKeywords)}
        canonical={seo.canonical}
        ogUrl={seo.ogUrl}
        ogImage={ogImageUrl(config.routeId, seo.locale)}
        locale={seo.locale}
        alternates={seo.alternates}
        jsonLd={jsonLd}
      />

      {/* ─── HEADER ─── */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-sm border-b border-gray-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 shrink-0">
              <Logo showText={true} imgClassName="h-12 md:h-20 w-auto object-contain" />
            </Link>

            {/* Anchor nav — desktop */}
            <nav className="hidden md:flex items-center gap-8 mx-auto">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => handleAnchorClick(e, link.href)}
                  className="text-sm font-medium text-gray-600 hover:text-primary-600 transition-colors"
                >
                  {link.label}
                </a>
              ))}
            </nav>

            {/* CTA — desktop */}
            <div className="hidden md:flex items-center gap-4 shrink-0">
              <LangSwitcher />
              <Link
                to="/login"
                className="px-4 py-2 text-sm font-semibold text-white bg-gradient-to-r from-primary-600 to-primary-700 rounded-lg hover:from-primary-700 hover:to-primary-800 transition-all shadow-sm"
              >
                {t('chrome.cta_register', 'Cadastrar Grátis')}
              </Link>
            </div>

            {/* Hamburger — mobile */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden ml-auto p-2 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors"
              aria-label={t('chrome.menu_aria', 'Menu')}
            >
              {mobileMenuOpen ? (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>

          {/* Mobile dropdown */}
          {mobileMenuOpen && (
            <div className="md:hidden border-t border-gray-100 py-4 space-y-1">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => handleAnchorClick(e, link.href)}
                  className="block px-2 py-2.5 text-sm font-medium text-gray-700 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                >
                  {link.label}
                </a>
              ))}
              <div className="pt-3 space-y-3">
                <div className="flex justify-center">
                  <LangSwitcher />
                </div>
                <Link
                  to="/login"
                  onClick={() => setMobileMenuOpen(false)}
                  className="w-full block text-center py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-primary-600 to-primary-700 rounded-lg hover:from-primary-700 hover:to-primary-800 transition-all"
                >
                  {t('chrome.cta_register', 'Cadastrar Grátis')}
                </Link>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* ─── HERO ─── */}
      <section className="bg-gradient-to-br from-primary-900 via-primary-800 to-primary-700 text-white py-20 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-1.5 mb-6">
            <Trophy className="w-4 h-4 text-yellow-300" />
            <span className="text-sm font-medium">{t('chrome.badge_free', '100% Gratuito')}</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-extrabold leading-tight mb-4">
            {ck('hero_title', config.heroTitle)}
          </h1>
          <p className="text-lg md:text-xl text-primary-100 mb-8 leading-relaxed">
            {ck('hero_subtitle', config.heroSubtitle)}
          </p>
          <Link
            to="/login"
            className="inline-flex items-center gap-2 px-8 py-4 bg-yellow-400 hover:bg-yellow-300 text-gray-900 font-bold text-lg rounded-2xl shadow-2xl hover:shadow-yellow-400/30 transition-all"
          >
            <Trophy className="w-5 h-5" />
            {t('chrome.hero_cta', 'Criar meu Bolão Grátis')}
          </Link>
          <p className="text-sm text-primary-200 mt-4">{t('chrome.hero_note', 'Sem cartão de crédito · Sem mensalidade')}</p>
        </div>
      </section>

      <AdSlot slotName="competition_landing_top" format="horizontal" area="public" className="max-w-5xl mx-auto px-4 py-6" />

      {/* ─── FATOS DA COMPETIÇÃO ─── */}
      {config.facts && config.facts.length > 0 && (
        <section id="sobre" className="py-14 px-4 bg-white border-b border-gray-100 scroll-mt-16">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 text-center">
              {t('chrome.facts_title', 'Sobre a competição')}
            </h2>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {config.facts.map((fact, i) => (
                <li key={i} className="flex items-start gap-3 bg-gray-50 rounded-xl p-4 border border-gray-100">
                  <CheckCircle className="w-5 h-5 text-primary-600 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-700 leading-relaxed">{ck(`fact_${i}`, fact)}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* ─── FUNCIONALIDADES ─── */}
      <section id="funcionalidades" className="py-16 px-4 bg-gray-50 scroll-mt-16">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-10">
            {t('chrome.features_title', 'Por que escolher o Bolão Elite?')}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {FEATURES.map(({ icon: Icon, titleKey, titleDefault, descKey, descDefault }) => (
              <div key={titleKey} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                <div className="w-10 h-10 bg-primary-50 rounded-xl flex items-center justify-center mb-3">
                  <Icon className="w-5 h-5 text-primary-600" />
                </div>
                <h3 className="font-bold text-gray-900 mb-1">{t(titleKey, titleDefault)}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{t(descKey, descDefault)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── COMO FUNCIONA ─── */}
      <section id="como-funciona" className="py-16 px-4 scroll-mt-16">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-10">
            {t('chrome.how_title', 'Como funciona?')}
          </h2>
          <div className="space-y-4">
            {HOW_STEPS.map(({ step, titleKey, titleDefault, descKey, descDefault }) => (
              <div key={step} className="flex gap-4 items-start">
                <div className="w-9 h-9 bg-primary-600 text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">
                  {step}
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">{t(titleKey, titleDefault)}</h3>
                  <p className="text-sm text-gray-500">{t(descKey, descDefault)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Social Proof / Benefícios */}
      <section className="py-12 px-4 bg-primary-50">
        <div className="max-w-3xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 text-center">
            {STATS.map(({ valueKey, valueDefault, labelKey, labelDefault }) => (
              <div key={labelKey} className="bg-white rounded-2xl border border-primary-100 p-5">
                <p className="text-2xl font-extrabold text-primary-600 mb-1">{t(valueKey, valueDefault)}</p>
                <p className="text-sm text-gray-500">{t(labelKey, labelDefault)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── FAQ ─── */}
      <section id="faq" className="py-16 px-4 bg-white scroll-mt-16">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-8">
            {t('chrome.faq_title', 'Perguntas frequentes')}
          </h2>
          <div className="space-y-3">
            {resolvedFaqs.map(faq => (
              <FaqItem key={faq.question} {...faq} />
            ))}
          </div>
        </div>
      </section>

      <AdSlot slotName="competition_landing_bottom" format="horizontal" area="public" className="max-w-5xl mx-auto px-4 py-6" />

      {/* ─── CTA FINAL ─── */}
      <section className="py-20 px-4 bg-gradient-to-br from-primary-900 via-primary-800 to-primary-700 text-white text-center">
        <div className="max-w-xl mx-auto">
          <h2 className="text-3xl font-extrabold mb-4">{t('chrome.final_cta_title', 'Pronto para começar?')}</h2>
          <p className="text-primary-100 mb-8">{t('chrome.final_cta_text', 'Crie seu bolão em minutos e compartilhe com amigos.')}</p>
          <Link
            to="/login"
            className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-yellow-400 hover:bg-yellow-300 text-gray-900 font-bold rounded-2xl shadow-xl transition-all"
          >
            <CheckCircle className="w-5 h-5" />
            {t('chrome.cta_register', 'Cadastrar Grátis')}
          </Link>
        </div>
      </section>

      {/* ─── OUTROS BOLÕES — interlinks SEO ─── */}
      <section className="py-14 px-4 bg-gray-50 border-t border-gray-200">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-2 text-center">
            {t('chrome.interlink_title', 'Explore outros bolões disponíveis')}
          </h2>
          <p className="text-sm text-gray-500 text-center mb-8">
            {t('chrome.interlink_text', 'O Bolão Elite suporta os principais campeonatos de futebol do Brasil e do mundo.')}
          </p>
          <ul className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {OTHER_COMPETITIONS.filter(c => c.slug !== config.slug).map((comp) => (
              <li key={comp.slug}>
                <Link
                  to={localizedPath(comp.routeId, locale)}
                  className="flex items-center justify-center text-center px-3 py-3 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-700 hover:border-primary-400 hover:text-primary-700 hover:bg-primary-50 transition-all"
                >
                  {t(comp.labelKey, comp.label)}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}

export default CompetitionLanding;
